import { useCallback, useState } from 'react'
import * as Sentry from '@sentry/react'
import { ContentViewerActions } from '@/content-viewer/store/slice'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { selectLoadedUserFilesById } from '@/user_files/files/store/selectors'
import { MedSummarySourceProvider, type MedSummarySourceRef } from './schemas'
import { openMedSummarySourceViewer, type OpenMedSummarySourceViewerOptions } from './open-med-summary-source-viewer'

type SourceLoadError = {
  sourceId: string
  message: string
} | null

function buildMedSummaryOpenTelemetry(source: MedSummarySourceRef, options: OpenMedSummarySourceViewerOptions) {
  return {
    tags: {
      contentViewerFeature: 'medical_billing_summary',
      sourceProvider: source.provider,
      referenceBacked: String(Boolean(options.legacyReference)),
    },
    extra: {
      sourceUserFileId: source.provider === MedSummarySourceProvider.USER_FILES ? source.source_id : null,
      legacyReferenceId: options.legacyReference?.id ?? null,
    },
  }
}

export function useOpenMedSummarySource() {
  const dispatch = useAppDispatch()
  const loadedUserFilesById = useAppSelector(selectLoadedUserFilesById)
  const [sourceLoadError, setSourceLoadError] = useState<SourceLoadError>(null)

  const openMedSummarySource = useCallback(
    async (source: MedSummarySourceRef, options: OpenMedSummarySourceViewerOptions = {}) => {
      setSourceLoadError(null)

      try {
        const result = await openMedSummarySourceViewer({
          source,
          options,
          loadedUserFilesById,
          dispatch,
        })

        if (!result.opened) {
          const telemetry = buildMedSummaryOpenTelemetry(source, options)
          Sentry.captureMessage('Medical billing summary source viewer open failed', {
            level: 'warning',
            tags: telemetry.tags,
            extra: {
              ...telemetry.extra,
              errorMessage: result.error,
            },
          })
          setSourceLoadError({ sourceId: source.source_id, message: result.error })
          return false
        }

        dispatch(ContentViewerActions.setContentViewerSession({ session: result.session }))
        return true
      } catch (error) {
        Sentry.captureException(error, buildMedSummaryOpenTelemetry(source, options))
        setSourceLoadError({ sourceId: source.source_id, message: 'Could not load this source file.' })
        return false
      }
    },
    [dispatch, loadedUserFilesById]
  )

  return {
    openMedSummarySource,
    sourceLoadError,
  }
}
